import React, { useState, useEffect } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import PrimaryButton from '../../components/PrimaryButton';
import { AppColors } from '../../styles/colors';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0 100px 0;
  gap: 24px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 16px;
  background-color: #f8f8f8;
  border-radius: 12px;
`;

const InfoIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #c4b5a0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  color: white;
`;

const InfoTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #262626;
  text-align: center;
`;

const InfoDescription = styled.div`
  font-size: 14px;
  color: #666;
  text-align: center;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #262626;
  margin-bottom: 16px;
`;

const AuctionInfoCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AuctionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`;

const AuctionMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: #666;
`;

const LotCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: #f8f8f8;
  border-radius: 8px;
  margin-top: 8px;
`;

const LotImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background-color: #e0e0e0;
`;

const LotImagePlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background-color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 12px;
`;

const LotInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
`;

const LotTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LotNumber = styled.div`
  font-size: 12px;
  color: #666;
`;

const LotTopInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LotPrice = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #69b5b0;
  margin-top: auto;
`;

const PaymentInfoCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PaymentInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const PaymentLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const PaymentValue = styled.div<{ $isHighlight?: boolean }>`
  font-size: 14px;
  font-weight: ${props => props.$isHighlight ? '700' : '500'};
  color: ${props => props.$isHighlight ? '#69b5b0' : '#262626'};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e9ecef;
  margin: 12px 0;
`;

const TotalPaymentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 8px 0;
`;

const TotalPaymentLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`;

const TotalPaymentValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #69b5b0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopyButton = styled.button`
  padding: 6px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background-color: #f0f0f0;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const NoticeBox = styled.div`
  background-color: #f8f5f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  gap: 12px;
`;

const NoticeIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
`;

const NoticeContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NoticeTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #282626;
`;

const NoticeText = styled.div`
  font-size: 13px;
  color: #282626;
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background-color: white;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  
  @media (min-width: 768px) {
    max-width: 1200px;
  }
`;

interface PaymentGuideTexts {
  title: string;
  infoTitle: string;
  infoDescription: string;
  wonInfoTitle: string;
  depositInfoTitle: string;
  paymentInfoTitle: string;
  paymentNumber: string;
  accountHolder: string;
  bankName: string;
  accountNumber: string;
  depositDeadline: string;
  auctionHouseFee: string;
  onlineAuctionFee: string;
  feeVAT: string;
  onlineFeeVAT: string;
  totalBidAmount: string;
  depositAmount: string;
  noticeTitle: string;
  noticeText: string;
  homeButton: string;
  copied: string;
  lots: string;
  copy: string;
}

const paymentGuideTexts: Record<'ko' | 'en', PaymentGuideTexts> = {
  ko: {
    title: '계좌이체 안내',
    infoTitle: '계좌이체 안내',
    infoDescription: '아래 계좌정보를 통해 입금 부탁드립니다.',
    wonInfoTitle: '낙찰 정보',
    depositInfoTitle: '입금정보',
    paymentInfoTitle: '결제정보',
    paymentNumber: '결제 번호',
    accountHolder: '예금주명',
    bankName: '은행명',
    accountNumber: '계좌번호',
    depositDeadline: '입금기한',
    auctionHouseFee: '경매장 수수료',
    onlineAuctionFee: '온라인경매 수수료',
    feeVAT: '경매장 수수료 부가세',
    onlineFeeVAT: '온라인경매 수수료 부가세',
    totalBidAmount: '총 낙찰가',
    depositAmount: '입금금액',
    noticeTitle: '안내사항',
    noticeText: '입금자명은 반드시 낙찰자 성함과 동일하게 입력해주세요.',
    homeButton: '홈으로 이동',
    copied: '복사되었습니다',
    lots: 'Lots',
    copy: '복사'
  },
  en: {
    title: 'Bank Transfer Guide',
    infoTitle: 'Bank Transfer Guide',
    infoDescription: 'Please deposit using the account information below.',
    wonInfoTitle: 'Won Information',
    depositInfoTitle: 'Deposit Information',
    paymentInfoTitle: 'Payment Information',
    paymentNumber: 'Payment Number',
    accountHolder: 'Account Holder',
    bankName: 'Bank Name',
    accountNumber: 'Account Number',
    depositDeadline: 'Payment Due',
    auctionHouseFee: 'Auction House Fee',
    onlineAuctionFee: 'Online Auction Fee',
    feeVAT: 'Fee VAT',
    onlineFeeVAT: 'Online Fee VAT',
    totalBidAmount: 'Total Bid Amount',
    depositAmount: 'Deposit Amount',
    noticeTitle: 'Notice',
    noticeText: 'Please enter the depositor\'s name exactly as the successful bidder\'s name.',
    homeButton: 'Go to Home',
    copied: 'Copied',
    lots: 'Lots',
    copy: 'Copy'
  }
};

const PaymentGuide: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const { user } = useUserAuth();
  const texts = paymentGuideTexts[lang];
  
  const { auctionData } = location.state || {};
  const isPaid = auctionData?.paymentStatus === 'PAID' || auctionData?.isPaid === true;
  
  useEffect(() => {
    devLog('🎯 [PaymentGuide] 페이지 진입:', { auctionData });
  }, [auctionData]);

  // 하드코딩된 결제 정보
  const paymentInfo = {
    paymentNumber: 'P2508050001',
    accountHolder: '(주) 린덴바움',
    bankName: '국민은행',
    accountNumber: '140-011-449560',
    depositDeadline: auctionData.paymentDueAt 
  };

  const handleCopyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(paymentInfo.accountNumber);
      toast.success(texts.copied);
    } catch (error) {
      devError('계좌번호 복사 실패:', error);
      toast.error('복사에 실패했습니다');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  // 수수료 계산 로직 (LiveBidRequest와 동일)
  const calculateTotalFees = (auctionData: any) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    let totalBidAmountKRW = 0;
    let totalBidAmountUSD = 0;
    
    // 총 낙찰가 계산
    if (auctionData.lots && Array.isArray(auctionData.lots)) {
      totalBidAmountKRW = auctionData.lots.reduce((sum: number, lot: any) => {
        return sum + (lot.amount || lot.price || 0);
      }, 0);
      totalBidAmountUSD = auctionData.lots.reduce((sum: number, lot: any) => {
        return sum + (lot.usd || 0);
      }, 0);
    } else {
      totalBidAmountKRW = auctionData.totalAmountKRW || auctionData.totalAmount || 0;
      totalBidAmountUSD = auctionData.totalAmountUSD || 0;
    }

    // 수수료율 (소켓 데이터에서 가져오거나 기본값 사용)
    const auctionHouseFeeRate = auctionData?.auctionFeeRate || 20; // 기본 20%
    const onlineAuctionFeeRate = auctionData?.platformFeeRate || 5; // 기본 5%
    const vatRate = 10; // 10% (고정)

    // KRW 수수료 계산
    const auctionHouseFeeKRW = Math.floor(totalBidAmountKRW * (auctionHouseFeeRate / 100));
    const onlineAuctionFeeKRW = Math.floor(totalBidAmountKRW * (onlineAuctionFeeRate / 100));
    const totalFeesKRW = auctionHouseFeeKRW + onlineAuctionFeeKRW;
    const feeVATKRW = isDomestic ? Math.floor(auctionHouseFeeKRW * (vatRate / 100)) : 0;
    const onlineFeeVATKRW = isDomestic ? Math.floor(onlineAuctionFeeKRW * (vatRate / 100)) : 0;
    const totalAmountKRW = totalBidAmountKRW + totalFeesKRW + feeVATKRW + onlineFeeVATKRW;

    // USD 수수료 계산 (해외 사용자용)
    const auctionHouseFeeUSD = Math.round(totalBidAmountUSD * (auctionHouseFeeRate / 100) * 100) / 100;
    const onlineAuctionFeeUSD = Math.round(totalBidAmountUSD * (onlineAuctionFeeRate / 100) * 100) / 100;
    const totalFeesUSD = auctionHouseFeeUSD + onlineAuctionFeeUSD;
    const feeVATUSD = isDomestic ? Math.round(auctionHouseFeeUSD * (vatRate / 100) * 100) / 100 : 0;
    const onlineFeeVATUSD = isDomestic ? Math.round(onlineAuctionFeeUSD * (vatRate / 100) * 100) / 100 : 0;
    const totalAmountUSD = Math.round((totalBidAmountUSD + totalFeesUSD + feeVATUSD + onlineFeeVATUSD) * 100) / 100;

    return {
      totalBidAmountKRW,
      totalBidAmountUSD,
      auctionHouseFeeKRW,
      onlineAuctionFeeKRW,
      auctionHouseFeeUSD,
      onlineAuctionFeeUSD,
      feeVATKRW,
      feeVATUSD,
      onlineFeeVATKRW,
      onlineFeeVATUSD,
      totalAmountKRW,
      totalAmountUSD,
      auctionHouseFeeRate,
      onlineAuctionFeeRate,
      vatRate
    };
  };

  const formatPrice = (amount: number, isUSD: boolean = false) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    if (isDomestic || !isUSD) {
      return `KRW ${amount.toLocaleString()}`;
    } else {
      return `USD ${amount.toLocaleString()}`;
    }
  };

  const formatLotPrice = (lot: any) => {
    const isDomestic = user?.locationType === 'DOMESTIC';
    if (isDomestic) {
      // 국내 사용자: KRW 금액 표시
      const amount = lot.amount || 0;
      return `KRW ${amount.toLocaleString()}`;
    } else {
      // 해외 사용자: USD 금액 표시
      const amount = lot.usd || lot.amount || 0;
      return `USD ${amount.toLocaleString()}`;
    }
  };

  const formatLotNumber = (lot: any) => {
    return `LOT${lot.index}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    // 이미 포맷된 문자열이면 그대로 반환
    if (dateString.includes('.') || dateString.includes('/')) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(2);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // 이미지 URL 처리 함수
  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/no_image.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  const isDomestic = user?.locationType === 'DOMESTIC';
  const feeCalculation = calculateTotalFees(auctionData);

  if (!auctionData) {
    return (
      <MainLayout>
        <CenteredLayout>
          <Container>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              데이터를 불러올 수 없습니다.
            </div>
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
  }

  const Layout = (
    <CenteredLayout>
      <ProjectAppbar title={texts.title} showBackButton currentPage="auction" />
      <Container>
        {/* 계좌이체 안내 정보 */}
        {!isPaid && (
          <InfoSection>
            <InfoIcon>i</InfoIcon>
            <InfoTitle>{texts.infoTitle}</InfoTitle>
            <InfoDescription>{texts.infoDescription}</InfoDescription>
          </InfoSection>
        )}

        {/* 낙찰 정보 */}
        <div>
          <SectionTitle>{texts.wonInfoTitle}</SectionTitle>
          <AuctionInfoCard>
            <AuctionTitle>{auctionData.name}</AuctionTitle>
            <AuctionMeta>
              <span>{auctionData.auctioneer?.name || 'Unknown'}</span>
              <span>{auctionData.totalWonLots || auctionData.lots?.length || 0} {texts.lots}</span>
            </AuctionMeta>
            
            {/* Lot 카드들 */}
            {auctionData.lots?.map((lot: any, index: number) => (
              <LotCard key={lot.index || index}>
                {lot.images && lot.images.length > 0 ? (
                  <LotImage 
                    src={getImageSrc(lot.images[0])}
                    alt={lot.name}
                  />
                ) : (
                  <LotImagePlaceholder>등록된 이미지 없음</LotImagePlaceholder>
                )}
                <LotInfo>
                  <LotTopInfo>
                    <LotTitle>{lot.name}</LotTitle>
                    <LotNumber>{formatLotNumber(lot)}</LotNumber>
                  </LotTopInfo>
                  <LotPrice>{formatLotPrice(lot)}</LotPrice>
                </LotInfo>
              </LotCard>
            ))}
          </AuctionInfoCard>
        </div>

        {/* 입금정보 */}
        <div>
          <SectionTitle>{texts.depositInfoTitle}</SectionTitle>
          <PaymentInfoCard>
            <PaymentInfoRow>
              <PaymentLabel>{texts.paymentNumber}</PaymentLabel>
              <PaymentValue>{paymentInfo.paymentNumber}</PaymentValue>
            </PaymentInfoRow>
            <PaymentInfoRow>
              <PaymentLabel>{texts.accountHolder}</PaymentLabel>
              <PaymentValue>{paymentInfo.accountHolder}</PaymentValue>
            </PaymentInfoRow>
            <PaymentInfoRow>
              <PaymentLabel>{texts.bankName}</PaymentLabel>
              <PaymentValue>{paymentInfo.bankName}</PaymentValue>
            </PaymentInfoRow>
            <PaymentInfoRow>
              <PaymentLabel>{texts.accountNumber}</PaymentLabel>
              <PaymentValue>
                {paymentInfo.accountNumber}
                <CopyButton onClick={handleCopyAccountNumber}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </CopyButton>
              </PaymentValue>
            </PaymentInfoRow>
            <PaymentInfoRow>
              <PaymentLabel>{texts.depositDeadline}</PaymentLabel>
              <PaymentValue>{formatDate(paymentInfo.depositDeadline)}</PaymentValue>
            </PaymentInfoRow>
          </PaymentInfoCard>
        </div>

        {/* 결제정보 */}
        <div>
          <SectionTitle>{texts.paymentInfoTitle}</SectionTitle>
          <PaymentInfoCard>
            <PaymentInfoRow>
              <PaymentLabel>{texts.totalBidAmount}</PaymentLabel>
              <PaymentValue>{isDomestic ? formatPrice(feeCalculation.totalBidAmountKRW) : formatPrice(feeCalculation.totalBidAmountUSD, true)}</PaymentValue>
            </PaymentInfoRow>
            <PaymentInfoRow>
              <PaymentLabel>{texts.auctionHouseFee} ({feeCalculation.auctionHouseFeeRate}%)</PaymentLabel>
              <PaymentValue>{isDomestic ? formatPrice(feeCalculation.auctionHouseFeeKRW) : formatPrice(feeCalculation.auctionHouseFeeUSD, true)}</PaymentValue>
            </PaymentInfoRow>
            <PaymentInfoRow>
              <PaymentLabel>{texts.onlineAuctionFee} ({feeCalculation.onlineAuctionFeeRate}%)</PaymentLabel>
              <PaymentValue>{isDomestic ? formatPrice(feeCalculation.onlineAuctionFeeKRW) : formatPrice(feeCalculation.onlineAuctionFeeUSD, true)}</PaymentValue>
            </PaymentInfoRow>
            {isDomestic && (
              <>
                <PaymentInfoRow>
                  <PaymentLabel>{texts.feeVAT} ({feeCalculation.vatRate}%)</PaymentLabel>
                  <PaymentValue>{formatPrice(feeCalculation.feeVATKRW)}</PaymentValue>
                </PaymentInfoRow>
                <PaymentInfoRow>
                  <PaymentLabel>{texts.onlineFeeVAT} ({feeCalculation.vatRate}%)</PaymentLabel>
                  <PaymentValue>{formatPrice(feeCalculation.onlineFeeVATKRW)}</PaymentValue>
                </PaymentInfoRow>
              </>
            )}
            <PaymentDivider />
            <TotalPaymentRow>
              <TotalPaymentLabel>{texts.depositAmount}</TotalPaymentLabel>
              <TotalPaymentValue>
                {isDomestic ? formatPrice(feeCalculation.totalAmountKRW) : formatPrice(feeCalculation.totalAmountUSD, true)}
              </TotalPaymentValue>
            </TotalPaymentRow>
          </PaymentInfoCard>
        </div>

        {/* 안내사항 */}
        {!isPaid && (
          <NoticeBox>
            <NoticeIcon>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <text x="6" y="9.5" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle">!</text>
              </svg>
            </NoticeIcon>
            <NoticeContent>
              <NoticeTitle>{texts.noticeTitle}</NoticeTitle>
              <NoticeText>• {texts.noticeText}</NoticeText>
            </NoticeContent>
          </NoticeBox>
        )}
      </Container>

      {/* 홈으로 이동 버튼 */}
      <ButtonContainer>
        <ButtonWrapper>
          <PrimaryButton onClick={handleGoHome}>
            {texts.homeButton}
          </PrimaryButton>
        </ButtonWrapper>
      </ButtonContainer>
    </CenteredLayout>
  );

  return (
    <MainLayout>
      <ResponsiveView
        mobileView={Layout}
        desktopView={Layout}
      />
    </MainLayout>
  );
};

export default PaymentGuide;
